import { useAutocomplete } from '@mui/base';
import { Typography, IconButton, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';

import CrossIcon from 'app/assets/icons/cross.svg?react';

export default function UseAutocomplete({
  value,
  onPostcodeChange,
  onChange,
  suggestions,
  address,
  loading,
}) {
  const { getRootProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } =
    useAutocomplete({
      getOptionLabel: (option) => {
        const { house, addressLine, city } = option;
        return [house, addressLine, city].filter(Boolean).join(', ');
      },
      options: suggestions || [],
      value,
      onChange: (event, newValue) => onChange(newValue),
      filterOptions: (x) => x,
      inputValue: value,
      onInputChange: (e) => {
        if (typeof e?.target?.value === 'string') {
          onPostcodeChange(e.target.value?.toUpperCase());
        }
      },
      isOptionEqualToValue: (option, inputValue) => !inputValue || option.postcode === inputValue,
    });

  function clearSelection() {
    onPostcodeChange('');
    onChange(null);
  }

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <StyledAutocompleteRoot {...getRootProps()}>
          <StyledInput {...getInputProps()} placeholder="Search by postcode" />
        </StyledAutocompleteRoot>
        {groupedOptions.length > 0 && (
          <StyledListbox {...getListboxProps()}>
            {groupedOptions.map((option, index) => (
              <StyledOption {...getOptionProps({ option, index })}>
                {[option.house, option.addressLine, option.city].filter(Boolean).join(', ')}
              </StyledOption>
            ))}
          </StyledListbox>
        )}
        {address && (
          <IconButton className="absolute right-12 top-14 p-0" onClick={clearSelection}>
            <CrossIcon className="text-typography-primary h-16" />
          </IconButton>
        )}
        {loading && (
          <CircularProgress
            className="absolute right-12 top-14 text-typography-primary"
            size={16}
          />
        )}
      </div>
      {address && (
        <Typography className="px-8 py-4 text-12 text-typography-primary font-500">
          {[address.house, address.addressLine, address.city, address.postcode]
            .filter(Boolean)
            .join(', ')}
        </Typography>
      )}
    </div>
  );
}

const StyledAutocompleteRoot = styled('div')(
  ({ theme }) => `
  position: relative;
  border-radius: 4px;
  background: transparent;
  border: 1px solid #E0E2E8;
  padding: 12px;
  width: 100%;
`
);

const StyledInput = styled('input')(
  ({ theme }) => `
  color: #24292f;
  background: inherit;
  border: none;
  outline: 0;
  width: 92%;
`
);

const StyledListbox = styled('ul')(
  ({ theme }) => `
  box-sizing: border-box;
  padding: 4px;
  overflow: auto;
  outline: 0px;
  max-height: 170px;
  z-index: 1;
  left: 0;
  right: 0;
  position: absolute;
  background: #fff;
  border: 1px solid #d0d7de;
  color: #24292f;
  box-shadow: 0px 4px 30px #d0d7de;
  `
);

const StyledOption = styled('li')(
  ({ theme }) => `
  list-style: none;
  padding: 8px;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    cursor: pointer;
  }

  &.Mui-focused,
  &.Mui-focusVisible {
    background-color: #f6f8fa;
    color: #24292f;
  }

  &.Mui-focusVisible {
    box-shadow: 0 0 0 3px #99CCF3;
  }

  &[aria-selected=true].Mui-focused,
  &[aria-selected=true].Mui-focusVisible {
    background-color: #DAECFF;
    color: #003A75;
  }
  `
);
